/**
 * What a deployed agent's run has done so far (the Progress card's steps), and
 * Stop. Steps are the run's tool calls as plain lines; they are read again
 * while the run is going and stop being read once it ends.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { POLL_MACHINE_MS, clampWhenHidden } from "@/lib/realtime";
import { queue } from "@/components/Toast";
import { workshopDeploymentRunsKey, workshopDeploymentsKey } from "./workshopKeys";

export type WorkshopRunStep = {
  line: string;
  /** When this step was recorded, so Progress can show it against the run start. */
  at: string;
};

export type WorkshopRunSteps = {
  runId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  done: boolean;
  summary: string | null;
  steps: WorkshopRunStep[];
  lastSeq: number;
};

export function useWorkshopRunSteps(deploymentId: string | null, runId: string | null) {
  return useQuery({
    queryKey: ["workshopRunSteps", deploymentId, runId],
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopRunSteps>(
        `/workshop/deployments/${deploymentId}/runs/${runId}/steps`,
      );
      return res.data;
    },
    enabled: !!deploymentId && !!runId,
    refetchInterval: (q) => (q.state.data?.done ? false : clampWhenHidden(POLL_MACHINE_MS)),
  });
}

/** Stop run: a queued run never starts; a running one ends at its next safe point. */
export function useStopWorkshopRun(projectId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { deploymentId: string; runId: string }) => {
      await axiosInstance.post(`/workshop/deployments/${vars.deploymentId}/runs/${vars.runId}/stop`);
    },
    onError: (error) =>
      queue.add({ title: "Couldn't stop the run", description: getAiRequestErrorMessage(error), variant: "error" }),
    onSettled: (_data, _error, vars) => {
      void qc.invalidateQueries({ queryKey: workshopDeploymentRunsKey(vars.deploymentId) });
      void qc.invalidateQueries({ queryKey: workshopDeploymentsKey(projectId) });
      void qc.invalidateQueries({ queryKey: ["workshopRunSteps", vars.deploymentId] });
    },
  });
}
